import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  TablePagination,
  TableSortLabel,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  AccountBalance as BankIcon,
} from '@mui/icons-material';
import { banksAPI } from '../services/api';

function BanksPage() {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState('asc');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    name: '',
    account_number: '',
    current_balance: '',
    currency: 'DZD',
  });

  useEffect(() => {
    loadBanks();
  }, []);

  const loadBanks = async () => {
    try {
      setLoading(true);
      const response = await banksAPI.getAll();
      setBanks(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Error loading banks:', err);
      setError('Failed to load banks. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (bank = null) => {
    if (bank) {
      setEditingBank(bank);
      setFormData({
        name: bank.name,
        account_number: bank.account_number || '',
        current_balance: bank.current_balance || '',
        currency: bank.currency || 'DZD',
      });
    } else {
      setEditingBank(null);
      setFormData({
        name: '',
        account_number: '',
        current_balance: '',
        currency: 'DZD',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingBank(null);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      showSnackbar('Le nom de la banque est requis.', 'error');
      return;
    }
    try {
      const payload = { ...formData };
      payload.name = payload.name.trim();
      payload.current_balance = payload.current_balance
        ? parseFloat(payload.current_balance)
        : 0;

      if (editingBank) {
        await banksAPI.update(editingBank.id, payload);
        showSnackbar('Bank updated successfully!', 'success');
      } else {
        await banksAPI.create(payload);
        showSnackbar('Bank created successfully!', 'success');
      }
      handleCloseDialog();
      loadBanks();
    } catch (err) {
      console.error('Error saving bank:', err);
      const errorMsg = err.response?.data
        ? JSON.stringify(err.response.data)
        : err.message;
      showSnackbar('Error saving bank: ' + errorMsg, 'error');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this bank? This will affect all associated checks.')) {
      try {
        await banksAPI.delete(id);
        showSnackbar('Bank deleted successfully!', 'success');
        loadBanks();
      } catch (err) {
        console.error('Error deleting bank:', err);
        showSnackbar('Error deleting bank: ' + (err.response?.data?.detail || err.message), 'error');
      }
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const descendingComparator = (a, b, orderBy) => {
    let aValue = a[orderBy];
    let bValue = b[orderBy];
    
    if (aValue === null || aValue === undefined) aValue = '';
    if (bValue === null || bValue === undefined) bValue = '';
    
    if (typeof aValue === 'string') aValue = aValue.toLowerCase();
    if (typeof bValue === 'string') bValue = bValue.toLowerCase();
    
    if (bValue < aValue) return -1;
    if (bValue > aValue) return 1;
    return 0;
  };

  const filteredBanks = banks.filter((bank) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      bank.name.toLowerCase().includes(searchLower) ||
      (bank.account_number && bank.account_number.toLowerCase().includes(searchLower))
    );
  }).sort(getComparator(order, orderBy));

  const paginatedBanks = filteredBanks.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Banks
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={loadBanks} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Bank
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 2, p: 2 }}>
        <TextField
          fullWidth
          placeholder="Search banks..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleRequestSort('name')}
                >
                  Bank Name
                </TableSortLabel>
              </TableCell>
              <TableCell>Account Number</TableCell>
              <TableCell>Current Balance</TableCell>
              <TableCell>Predicted Balance</TableCell>
              <TableCell>Pending Outgoing</TableCell>
              <TableCell>Pending Incoming</TableCell>
              <TableCell>Currency</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedBanks.map((bank) => (
              <TableRow key={bank.id} hover>
                <TableCell>{bank.name}</TableCell>
                <TableCell>{bank.account_number || '-'}</TableCell>
                <TableCell>
                  {bank.current_balance !== null && bank.current_balance !== undefined
                    ? bank.current_balance.toLocaleString()
                    : '-'}
                </TableCell>
                <TableCell>
                  {bank.predicted_balance !== null && bank.predicted_balance !== undefined
                    ? parseFloat(bank.predicted_balance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    : '-'}
                </TableCell>
                <TableCell>
                  {bank.pending_outgoing_total !== null && bank.pending_outgoing_total !== undefined
                    ? parseFloat(bank.pending_outgoing_total).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    : '-'}
                </TableCell>
                <TableCell>
                  {bank.pending_incoming_total !== null && bank.pending_incoming_total !== undefined
                    ? parseFloat(bank.pending_incoming_total).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    : '-'}
                </TableCell>
                <TableCell>{bank.currency || '-'}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(bank)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(bank.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {paginatedBanks.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No banks found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredBanks.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingBank ? 'Edit Bank' : 'Add New Bank'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Bank Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Account Number"
              value={formData.account_number}
              onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
              fullWidth
            />
            <TextField
              label="Current Balance"
              type="number"
              value={formData.current_balance}
              onChange={(e) => setFormData({ ...formData, current_balance: e.target.value })}
              fullWidth
            />
            <TextField
              label="Currency"
              value={formData.currency}
              onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name}
          >
            {editingBank ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default BanksPage;
